import * as z from "zod/v4";
import { PendingUpdateSchema } from "./agents/platform-agents/settings/pending-update";

// Messages from the customer portal iframe to the merchant app
const CustomerPortalReadyMessage = z.object({ type: z.literal("ready") });

const CustomerPortalNavigateMessage = z.object({
  type: z.literal("navigate"),
  path: z.string(),
});

const CustomerPortalAiResponseMessage = z.object({
  type: z.literal("aiResponse"),
  message: z.string(),
});

const CustomerPortalErrorMessage = z.object({
  type: z.literal("error"),
  error: z.string(),
});

const CustomerPortalDomContextMessage = z.object({
  type: z.literal("domContext"),
  dom: z.string(),
  styles: z.string().optional(),
});

const CustomerPortalMessageSchema_ = z.discriminatedUnion("type", [
  CustomerPortalReadyMessage,
  CustomerPortalNavigateMessage,
  CustomerPortalAiResponseMessage,
  CustomerPortalErrorMessage,
  CustomerPortalDomContextMessage,
]);

export const CustomerPortalMessageSchema: z.ZodType<CustomerPortalMessage> =
  CustomerPortalMessageSchema_;
export type CustomerPortalMessage = z.infer<
  typeof CustomerPortalMessageSchema_
>;

// Messages from the merchant app to the customer portal iframe
const MerchantAppNavigateMessage = z.object({
  type: z.literal("navigate"),
  path: z.string(),
});

const MerchantAppAiCommandMessage = z.object({
  type: z.literal("aiCommand"),
  command: z.string(),
  data: z.record(z.string(), z.unknown()).optional(),
});

const MerchantAppRefreshMessage = z.object({ type: z.literal("refresh") });

const MerchantAppSettingsUpdateMessage = z.object({
  type: z.literal("settingsUpdate"),
  updates: z.array(PendingUpdateSchema),
});

const MerchantAppRequestDomMessage = z.object({
  type: z.literal("requestDom"),
});

const MerchantAppMessageSchema_ = z.discriminatedUnion("type", [
  MerchantAppNavigateMessage,
  MerchantAppAiCommandMessage,
  MerchantAppRefreshMessage,
  MerchantAppSettingsUpdateMessage,
  MerchantAppRequestDomMessage,
]);

export const MerchantAppMessageSchema: z.ZodType<MerchantAppMessage> =
  MerchantAppMessageSchema_;
export type MerchantAppMessage = z.infer<typeof MerchantAppMessageSchema_>;

// Helper to wrap messages with a source identifier for postMessage
export const PORTAL_BUILDER_MESSAGE_SOURCE = "redo-portal-builder";

export interface WrappedPortalBuilderMessage<T> {
  source: typeof PORTAL_BUILDER_MESSAGE_SOURCE;
  payload: T;
}

export function wrapMessage<
  T extends CustomerPortalMessage | MerchantAppMessage,
>(message: T): WrappedPortalBuilderMessage<T> {
  return { source: PORTAL_BUILDER_MESSAGE_SOURCE, payload: message };
}

export function isPortalBuilderMessage(
  data: unknown,
): data is WrappedPortalBuilderMessage<
  CustomerPortalMessage | MerchantAppMessage
> {
  return (
    typeof data === "object" &&
    data !== null &&
    "source" in data &&
    (data as { source: unknown }).source === PORTAL_BUILDER_MESSAGE_SOURCE &&
    "payload" in data
  );
}
